import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { pageRootTestIds } from "../../testing/pageRootTestIds";
import CertificateUploadStep, {
  SLOT_KEYS,
} from "../../common/CertificateOnboarding/components/CertificateUploadStep";
import CertificateConfigureStep from "../../common/CertificateOnboarding/components/CertificateConfigureStep";
import CertificatePreviewStep from "../../common/CertificateOnboarding/components/CertificatePreviewStep";
import CertificateCompleteStep from "../../common/CertificateOnboarding/components/CertificateCompleteStep";
import CertificateOnboardSuccessModal from "../../common/CertificateOnboarding/components/CertificateOnboardSuccessModal";
import CertificateOnboardingHeader from "../../common/CertificateOnboarding/components/CertificateOnboardingHeader";
import CertificateOnboardingStepper from "../../common/CertificateOnboarding/components/CertificateOnboardingStepper";
import FontPreparationSection from "../../common/CertificateOnboarding/components/FontPreparationSection";
import LeaveConfirmDialog from "../../common/CertificateOnboarding/components/LeaveConfirmDialog";
import FullScreenLoadingOverlay from "../../common/FullScreenLoadingOverlay/FullScreenLoadingOverlay";
import AuthContext from "../../store/auth-context";
import CertificateMetadataForm, {
  INITIAL_METADATA,
} from "../../common/CertificateOnboarding/components/CertificateMetadataForm";
import CredentialFormatPicker from "../../common/CertificateOnboarding/components/CredentialFormatPicker";
import EntityConstants from "../../configs/EntityConstants";
import { GET, POST, POSTFORMDATA } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import {
  ISSUER_PROJECT,
  buildCertificateCleanupPayload,
  parseIssuerProject,
} from "../../common/CertificateOnboarding/utils/issuerProject";
import {
  cloneVariableConfig,
  QR_CODE_TYPE_API,
  serializeVariableConfigForApi,
} from "../../common/CertificateOnboarding/utils/variables";
import { hasWhitespace } from "../../common/CertificateOnboarding/utils/entityName";
import { buildQrCodeOptions } from "../../common/CertificateOnboarding/utils/qr";
import { PRIMARY_BUTTON_CLASS } from "../../common/CertificateOnboarding/utils/styles";
import { scrollToElement } from "../../common/CertificateOnboarding/utils/scrollToElement";
import { hasMetadataInput } from "../../common/CertificateOnboarding/utils/unsavedWork";
import { isOnLeaveGuardEntry, useLeaveGuard } from "../../hooks/useLeaveGuard";

const INITIAL_ASSETS = {
  [SLOT_KEYS.previewSvg]: null,
  [SLOT_KEYS.templateSvg]: null,
};

const INITIAL_UPLOADING = {
  [SLOT_KEYS.previewSvg]: false,
  [SLOT_KEYS.templateSvg]: false,
};

const TOTAL_ASSETS = 2;

function isSuccessfulApiResponse(response, data) {
  if (!response?.ok) return false;
  if (data?.status_code != null && Number(data.status_code) !== 200) {
    return false;
  }
  return true;
}

function getApiErrorMessage(data, fallback) {
  return data?.message || data?.error || fallback;
}

function CertificateOnboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const isTenantAdmin = authContext.userType?.toLowerCase() === "tenant_admin";
  const tenantCode =
    authContext.tenantCode || localStorage.getItem("tenantCode") || "";

  const [onboardingScope, setOnboardingScope] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isOnboardingSubmitting, setIsOnboardingSubmitting] = useState(false);
  const [onboardResult, setOnboardResult] = useState(null);
  const [isOnboardSuccessModalOpen, setIsOnboardSuccessModalOpen] =
    useState(false);
  const [step, setStep] = useState(1);
  const [assets, setAssets] = useState(INITIAL_ASSETS);
  const [uploadingSlots, setUploadingSlots] = useState(INITIAL_UPLOADING);
  const [metadata, setMetadata] = useState(INITIAL_METADATA);
  const [metadataErrors, setMetadataErrors] = useState({});
  const [showMetadataErrors, setShowMetadataErrors] = useState(false);
  const [cleanupData, setCleanupData] = useState(null);
  const [variableConfig, setVariableConfig] = useState({});
  const [userAddedVariableKeys, setUserAddedVariableKeys] = useState([]);
  const [qrCodeType, setQrCodeType] = useState(QR_CODE_TYPE_API.ENCODED);
  const [updateData, setUpdateData] = useState(null);
  const [createFormData, setCreateFormData] = useState(null);
  const [isCleanupLoading, setIsCleanupLoading] = useState(false);
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [isCreateFormLoading, setIsCreateFormLoading] = useState(false);
  const [entitySuggestions, setEntitySuggestions] = useState([]);
  const [isSuggestionsLoading, setIsSuggestionsLoading] = useState(false);
  const [suggestionsError, setSuggestionsError] = useState(null);
  const [isEntityNameValidating, setIsEntityNameValidating] = useState(false);
  const [entityNameValidation, setEntityNameValidation] = useState(null);
  const [fontPrepData, setFontPrepData] = useState(null);
  const [isFontPrepLoading, setIsFontPrepLoading] = useState(false);
  const [fontPrepError, setFontPrepError] = useState(null);
  const [manualFontCss, setManualFontCss] = useState(null);
  const [isManualFontUploading, setIsManualFontUploading] = useState(false);
  const [issuerProject, setIssuerProject] = useState(null);
  const [isIssuerProjectLoading, setIsIssuerProjectLoading] = useState(true);
  const [issuerProjectError, setIssuerProjectError] = useState(false);
  const [credentialFormat, setCredentialFormat] = useState(null);
  const [showFormatError, setShowFormatError] = useState(false);
  const [isLeaveDialogOpen, setIsLeaveDialogOpen] = useState(false);
  const latestIssuerProjectRequestRef = useRef(0);
  const latestEntityValidationRequestRef = useRef(0);
  const latestFolderNameRef = useRef(INITIAL_METADATA.folderName);

  const uploadedCount = useMemo(() => {
    return Object.values(assets).filter(Boolean).length;
  }, [assets]);

  const allAssetsUploaded = uploadedCount === TOTAL_ASSETS;
  const isAnyUploading = Object.values(uploadingSlots).some(Boolean);
  const isEntityNameValidated = entityNameValidation?.available === true;
  const isFontPrepComplete = fontPrepData !== null;
  const canDetectFonts =
    allAssetsUploaded && isEntityNameValidated && !isFontPrepLoading;
  const canFetchSuggestions =
    Boolean(metadata.certificateName?.trim()) &&
    Boolean(metadata.certificateDescription?.trim());
  const isMetadataComplete =
    Boolean(metadata.folderName?.trim()) &&
    Boolean(metadata.certificateName?.trim()) &&
    Boolean(metadata.certificateDescription?.trim()) &&
    isEntityNameValidated;
  const canContinueStep1 =
    allAssetsUploaded &&
    !isAnyUploading &&
    isMetadataComplete &&
    isFontPrepComplete &&
    !isIssuerProjectLoading &&
    !isCleanupLoading;

  const hasTransactionId = Boolean(cleanupData?.transaction_id);
  const canContinueStep2 =
    !isUpdateLoading &&
    Object.keys(variableConfig).length > 0 &&
    hasTransactionId;

  const previewTransactionId =
    updateData?.transaction_id || cleanupData?.transaction_id || "";

  const canCreateFormSchema =
    !isCreateFormLoading && Boolean(previewTransactionId);

  const canOnboardCertificate =
    Boolean(createFormData) &&
    Boolean(onboardingScope) &&
    Boolean(selectedCategory) &&
    !onboardResult &&
    !isOnboardingSubmitting;

  const contentTitle = useMemo(() => {
    if (step === 1) return t("certificateOnboarding:content_title_step1");
    if (step === 2) return t("certificateOnboarding:content_title_step2");
    if (step === 3) return t("certificateOnboarding:content_title_step3");
    return t("certificateOnboarding:content_title_step4");
  }, [step, t]);

  const contentSubtitle = useMemo(() => {
    if (step === 1) return t("certificateOnboarding:page_subtitle");
    if (step === 2)
      return t("certificateOnboarding:step_configure_description");
    if (step === 3) return t("certificateOnboarding:step_preview_description");
    return t("certificateOnboarding:step_complete_description");
  }, [step, t]);

  const issuerProjectErrorTitle = t("certificateOnboarding:error");
  const issuerProjectFailedMessage = t(
    "certificateOnboarding:issuer_project_failed",
  );

  const handleScopeChange = (scope) => {
    setOnboardingScope(scope);
    setSelectedCategory(null);
  };

  const handleAddVariable = (key, attrs) => {
    setVariableConfig((prev) => ({
      ...prev,
      [key]: attrs,
    }));
    setUserAddedVariableKeys((prev) =>
      prev.includes(key) ? prev : [...prev, key],
    );
  };

  const handleRemoveVariable = (key) => {
    setVariableConfig((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setUserAddedVariableKeys((prev) => prev.filter((item) => item !== key));
  };

  const handleExitOnboarding = () => {
    const options = { replace: isOnLeaveGuardEntry() };
    if (isTenantAdmin && tenantCode) {
      navigate(`/issuer/tenant/${tenantCode}/organisations/users`, options);
      return;
    }
    navigate("/issuer/tenant", options);
  };

  const hasUnsavedWork =
    !onboardResult &&
    (step > 1 ||
      Boolean(cleanupData) ||
      uploadedCount > 0 ||
      hasMetadataInput(metadata) ||
      Boolean(credentialFormat) ||
      Boolean(fontPrepData) ||
      Boolean(manualFontCss));

  const leaveGuard = useLeaveGuard({
    when: hasUnsavedWork,
    onAttempt: () => setIsLeaveDialogOpen(true),
  });

  const handleRequestExit = () => {
    if (hasUnsavedWork) {
      setIsLeaveDialogOpen(true);
      return;
    }
    handleExitOnboarding();
  };

  const fetchIssuerProject = useCallback(async () => {
    const requestId = ++latestIssuerProjectRequestRef.current;
    setIsIssuerProjectLoading(true);
    setIssuerProjectError(false);
    setIssuerProject(null);

    try {
      const response = await GET(EntityConstants.ISSUER_PROJECT);
      const data = await response.json();
      if (requestId !== latestIssuerProjectRequestRef.current) return;

      const project = isSuccessfulApiResponse(response, data)
        ? parseIssuerProject(data)
        : null;
      if (!project) {
        setIssuerProjectError(true);
        Toast({
          type: "error",
          title: issuerProjectErrorTitle,
          message: issuerProjectFailedMessage,
        });
        return;
      }

      setIssuerProject(project);
    } catch (error) {
      console.error("Failed to load issuer project", error);
      if (requestId === latestIssuerProjectRequestRef.current) {
        setIssuerProjectError(true);
        Toast({
          type: "error",
          title: issuerProjectErrorTitle,
          message: issuerProjectFailedMessage,
        });
      }
    } finally {
      if (requestId === latestIssuerProjectRequestRef.current) {
        setIsIssuerProjectLoading(false);
      }
    }
  }, [issuerProjectErrorTitle, issuerProjectFailedMessage]);

  useEffect(() => {
    fetchIssuerProject();
    return () => {
      latestIssuerProjectRequestRef.current += 1;
    };
  }, [fetchIssuerProject]);

  const handleOnboardCertificate = async () => {
    if (!canOnboardCertificate) return;

    setIsOnboardingSubmitting(true);
    try {
      const payload = {
        transaction_id: createFormData.transaction_id || "",
        category_id: selectedCategory.category_id || "",
        template_form_id: createFormData.template_form_id || "",
        subject_form_id: createFormData.subject_form_id || "",
        folder_name:
          createFormData.entity_name?.trim() ||
          metadata.folderName?.trim() ||
          "",
        credential_name:
          createFormData.certificate_name?.trim() ||
          metadata.certificateName?.trim() ||
          "",
        description:
          createFormData.certificate_description?.trim() ||
          metadata.certificateDescription?.trim() ||
          "",
        qr_code_options: createFormData.qr_code_options || {},
        credential_schema: createFormData.sunbirdrc_schema_json || {},
        credential_context: createFormData.context_json || {},
        subject_properties:
          createFormData.credential_subject_properties_json || {},
      };
      if (onboardingScope.type === "tenant" && onboardingScope.code) {
        payload.tenant_id = onboardingScope.code;
      } else if (
        onboardingScope.type === "organisation" &&
        onboardingScope.code
      ) {
        payload.organisation_id = [onboardingScope.code];
      }

      const response = await POST(
        EntityConstants.CERTIFICATE_ONBOARDING_CREATE_CREDENTIALS,
        payload,
      );
      const data = await response.json();

      if (!isSuccessfulApiResponse(response, data)) {
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: getApiErrorMessage(
            data,
            t("certificateOnboarding:onboard_certificate_failed"),
          ),
        });
        return;
      }

      const result = data?.data ?? data ?? {};
      setOnboardResult({
        template_id: result.template_id || "",
        template_ids: result.template_ids || [],
      });
      setIsOnboardSuccessModalOpen(true);

      Toast({
        type: "success",
        title: t("certificateOnboarding:success"),
        message:
          data.message ||
          t("certificateOnboarding:onboard_certificate_success"),
      });
    } catch (error) {
      console.error("Certificate onboard failed", error);
      Toast({
        type: "error",
        title: t("certificateOnboarding:error"),
        message: t("certificateOnboarding:onboard_certificate_failed"),
      });
    } finally {
      setIsOnboardingSubmitting(false);
    }
  };
  const invalidateFontPrep = useCallback(() => {
    setFontPrepData(null);
    setFontPrepError(null);
  }, []);

  const handleMetadataChange = (event) => {
    const { name, value } = event.target;
    setMetadata((prev) => ({ ...prev, [name]: value }));
    if (showMetadataErrors && metadataErrors[name]) {
      setMetadataErrors((prev) => ({ ...prev, [name]: null }));
    }
    if (name === "certificateName" || name === "certificateDescription") {
      setSuggestionsError(null);
      setEntitySuggestions([]);
      setEntityNameValidation(null);
      latestFolderNameRef.current = "";
      setMetadata((prev) => ({ ...prev, folderName: "" }));
      invalidateFontPrep();
    }
  };

  const handleFolderNameChange = useCallback(
    (value) => {
      latestFolderNameRef.current = value;
      setMetadata((prev) => ({ ...prev, folderName: value }));
      setEntityNameValidation(null);
      setMetadataErrors((prev) => ({ ...prev, folderName: null }));
      invalidateFontPrep();
    },
    [invalidateFontPrep],
  );

  const handleFetchEntitySuggestions = useCallback(async () => {
    const certName = metadata.certificateName?.trim();
    const certDesc = metadata.certificateDescription?.trim();
    if (!certName || !certDesc) return;

    setIsSuggestionsLoading(true);
    setSuggestionsError(null);

    try {
      const response = await POST(
        EntityConstants.CERTIFICATE_ONBOARDING_SUGGEST_ENTITY_NAMES,
        {
          certificate_name: certName,
          certificate_description: certDesc,
        },
      );
      const data = await response.json();

      if (!isSuccessfulApiResponse(response, data)) {
        setSuggestionsError(
          getApiErrorMessage(
            data,
            t("certificateOnboarding:entity_name_suggestions_failed"),
          ),
        );
        return;
      }

      const suggestions = data?.data?.suggestions || [];
      setEntitySuggestions(suggestions);
    } catch (error) {
      console.error("Entity name suggestions failed", error);
      setSuggestionsError(
        t("certificateOnboarding:entity_name_suggestions_failed"),
      );
    } finally {
      setIsSuggestionsLoading(false);
    }
  }, [metadata.certificateName, metadata.certificateDescription, t]);

  const { certificateName, certificateDescription } = metadata;
  React.useEffect(() => {
    const certName = certificateName?.trim();
    const certDesc = certificateDescription?.trim();
    if (!certName || !certDesc) return;

    const timer = setTimeout(() => {
      handleFetchEntitySuggestions();
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificateName, certificateDescription]);

  const handleValidateEntityName = useCallback(
    async (name) => {
      const trimmed = name?.trim();
      if (!trimmed) return;

      const requestId = ++latestEntityValidationRequestRef.current;

      setIsEntityNameValidating(true);
      setEntityNameValidation(null);

      try {
        const response = await POST(
          EntityConstants.CERTIFICATE_ONBOARDING_VALIDATE_ENTITY_NAME,
          { entity_name: trimmed },
        );
        const data = await response.json();

        if (requestId !== latestEntityValidationRequestRef.current) return;

        if (data?.code === "token_not_valid" || data?.detail) {
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message:
              data.detail ||
              t("certificateOnboarding:entity_name_validation_failed"),
          });
          setEntityNameValidation(null);
          return;
        }

        if (!isSuccessfulApiResponse(response, data)) {
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message: getApiErrorMessage(
              data,
              t("certificateOnboarding:entity_name_validation_failed"),
            ),
          });
          setEntityNameValidation(null);
          return;
        }

        if (trimmed !== latestFolderNameRef.current?.trim()) return;

        const result = data?.data || {};
        setEntityNameValidation({
          available: result.available === true,
          message: result.message || "",
        });
      } catch (error) {
        console.error("Entity name validation failed", error);
        if (requestId !== latestEntityValidationRequestRef.current) return;
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: t("certificateOnboarding:entity_name_validation_failed"),
        });
        setEntityNameValidation(null);
      } finally {
        if (requestId === latestEntityValidationRequestRef.current) {
          setIsEntityNameValidating(false);
        }
      }
    },
    [t],
  );

  const handlePrepareFonts = useCallback(
    async (overrideCssUrl) => {
      const entityName = metadata.folderName?.trim();
      const getAssetUrl = (asset) => asset?.file_url || asset?.url || "";
      const previewUrl = getAssetUrl(assets.previewSvg);
      const templateUrl = getAssetUrl(assets.templateSvg);

      if (!entityName || !previewUrl || !templateUrl) return;

      setIsFontPrepLoading(true);
      setFontPrepError(null);

      try {
        const payload = {
          entity_name: entityName,
          preview_svg_url: previewUrl,
          template_svg_url: templateUrl,
        };

        const manualCssUrl = overrideCssUrl || getAssetUrl(manualFontCss);
        if (manualCssUrl) {
          payload.font_styles = manualCssUrl;
        }

        const response = await POST(
          EntityConstants.CERTIFICATE_ONBOARDING_PREPARE_FONTS,
          payload,
        );
        const data = await response.json();

        if (!isSuccessfulApiResponse(response, data)) {
          setFontPrepError(
            getApiErrorMessage(
              data,
              t("certificateOnboarding:font_prep_failed"),
            ),
          );
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message: getApiErrorMessage(
              data,
              t("certificateOnboarding:font_prep_failed"),
            ),
          });
          return;
        }

        const result = data?.data || {};
        setFontPrepData({
          font_styles: result.font_styles || "",
          required_fonts: result.required_fonts || {},
          resolved_fonts: result.resolved_fonts || [],
          missing_fonts: result.missing_fonts || [],
        });

        Toast({
          type: "success",
          title: t("certificateOnboarding:success"),
          message: data.message || t("certificateOnboarding:font_prep_success"),
        });
      } catch (error) {
        console.error("Font preparation failed", error);
        setFontPrepError(t("certificateOnboarding:font_prep_failed"));
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: t("certificateOnboarding:font_prep_failed"),
        });
      } finally {
        setIsFontPrepLoading(false);
      }
    },
    [
      metadata.folderName,
      assets.previewSvg,
      assets.templateSvg,
      manualFontCss,
      t,
    ],
  );

  React.useEffect(() => {
    const getAssetUrl = (asset) => asset?.file_url || asset?.url || "";
    const previewUrl = getAssetUrl(assets.previewSvg);
    const templateUrl = getAssetUrl(assets.templateSvg);

    if (
      previewUrl &&
      templateUrl &&
      isEntityNameValidated &&
      fontPrepData === null &&
      !isFontPrepLoading
    ) {
      handlePrepareFonts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets.previewSvg, assets.templateSvg, isEntityNameValidated]);

  const prevPreviewSvgRef = useRef(assets.previewSvg);
  const prevTemplateSvgRef = useRef(assets.templateSvg);
  useEffect(() => {
    if (
      prevPreviewSvgRef.current !== assets.previewSvg ||
      prevTemplateSvgRef.current !== assets.templateSvg
    ) {
      invalidateFontPrep();
    }
    prevPreviewSvgRef.current = assets.previewSvg;
    prevTemplateSvgRef.current = assets.templateSvg;
  }, [assets.previewSvg, assets.templateSvg, invalidateFontPrep]);

  const prevAllUploadedRef = useRef(allAssetsUploaded);

  const handleDescriptionBlur = useCallback(() => {
    if (step === 1 && canFetchSuggestions) {
      scrollToElement("cert-onboarding-entity-name-picker");
    }
  }, [step, canFetchSuggestions]);

  useEffect(() => {
    if (step !== 1) return;
    let cancelScroll;
    if (allAssetsUploaded && !prevAllUploadedRef.current) {
      cancelScroll = scrollToElement("cert-onboarding-font-preparation");
    }
    prevAllUploadedRef.current = allAssetsUploaded;
    return () => cancelScroll?.();
  }, [allAssetsUploaded, step]);

  const handleManualFontUpload = useCallback(
    async (file) => {
      setIsManualFontUploading(true);

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await POSTFORMDATA(
          EntityConstants.CERTIFICATE_ONBOARDING_UPLOAD,
          formData,
        );
        const data = await response.json();

        if (!isSuccessfulApiResponse(response, data)) {
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message: getApiErrorMessage(
              data,
              t("certificateOnboarding:upload_failed"),
            ),
          });
          return;
        }

        const uploaded = data.data;
        if (!uploaded?.file_url && !uploaded?.url) {
          Toast({
            type: "error",
            title: t("certificateOnboarding:error"),
            message: t("certificateOnboarding:upload_failed"),
          });
          return;
        }

        setManualFontCss({
          file_name: uploaded.file_name,
          file_url: uploaded.file_url,
          url: uploaded.url,
        });

        Toast({
          type: "success",
          title: t("certificateOnboarding:success"),
          message: data.message || t("certificateOnboarding:upload_success"),
        });

        const freshCssUrl = uploaded.file_url || uploaded.url || "";
        await handlePrepareFonts(freshCssUrl);
      } catch (error) {
        console.error("Manual font CSS upload failed", error);
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: t("certificateOnboarding:upload_failed"),
        });
      } finally {
        setIsManualFontUploading(false);
      }
    },
    [t, handlePrepareFonts],
  );

  const handleManualFontRemove = useCallback(() => {
    setManualFontCss(null);
    invalidateFontPrep();
  }, [invalidateFontPrep]);

  const handleContinueStep1 = async () => {
    const requiredMessage = t("certificateOnboarding:field_required");
    const errors = {};
    if (!issuerProject || isIssuerProjectLoading) return;
    if (issuerProject === ISSUER_PROJECT.CERTIFY && !credentialFormat) {
      setShowFormatError(true);
      scrollToElement("cert-onboarding-credential-format");
      return;
    }
    if (!metadata.certificateName?.trim())
      errors.certificateName = requiredMessage;
    if (!metadata.certificateDescription?.trim())
      errors.certificateDescription = requiredMessage;
    if (!metadata.folderName?.trim()) {
      errors.folderName = t("certificateOnboarding:entity_name_required");
    } else if (hasWhitespace(metadata.folderName.trim())) {
      errors.folderName = t(
        "certificateOnboarding:entity_name_no_spaces_error",
      );
    } else if (!isEntityNameValidated) {
      errors.folderName = t("certificateOnboarding:entity_name_not_validated");
    }
    if (Object.keys(errors).length > 0) {
      setMetadataErrors(errors);
      setShowMetadataErrors(true);
      return;
    }
    if (!canContinueStep1) return;

    setShowMetadataErrors(false);
    setMetadataErrors({});
    setIsCleanupLoading(true);

    try {
      const getAssetUrl = (asset) => asset?.file_url || asset?.url || "";

      const fontStylesUrl =
        fontPrepData?.font_styles || getAssetUrl(manualFontCss) || "";

      const payload = buildCertificateCleanupPayload({
        issuerProject,
        credentialFormat,
        metadata,
        assets,
        fontStylesUrl,
        getAssetUrl,
      });
      const response = await POST(
        EntityConstants.CERTIFICATE_ONBOARDING_CLEANUP,
        payload,
      );
      const data = await response.json();

      if (!isSuccessfulApiResponse(response, data)) {
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: getApiErrorMessage(
            data,
            t("certificateOnboarding:cleanup_failed"),
          ),
        });
        return;
      }

      const cleanupResult = data?.data ?? data ?? {};
      setCleanupData({
        transaction_id: cleanupResult.transaction_id || "",
        preview_svg_url:
          cleanupResult.preview_svg_url || getAssetUrl(assets.previewSvg),
        template_svg_url:
          cleanupResult.template_svg_url || getAssetUrl(assets.templateSvg),
        font_styles: cleanupResult.font_styles || fontStylesUrl,
        template_variables: cleanupResult.template_variables || {},
        credential_format:
          issuerProject === ISSUER_PROJECT.CERTIFY ? credentialFormat : null,
      });
      setVariableConfig(
        cloneVariableConfig(cleanupResult.template_variables || {}),
      );
      setUserAddedVariableKeys([]);
      setStep(2);
    } catch (error) {
      console.error("Certificate cleanup failed", error);
      Toast({
        type: "error",
        title: t("certificateOnboarding:error"),
        message: t("certificateOnboarding:cleanup_failed"),
      });
    } finally {
      setIsCleanupLoading(false);
    }
  };

  const handleContinueStep2 = async () => {
    if (!canContinueStep2) return;

    setIsUpdateLoading(true);

    try {
      const payload = {
        transaction_id: cleanupData?.transaction_id || "",
        template_variables: serializeVariableConfigForApi(variableConfig),
        qr_code_options: buildQrCodeOptions(variableConfig),
        qr_code_type: qrCodeType || "encoded",
      };
      const response = await POST(
        EntityConstants.CERTIFICATE_ONBOARDING_UPDATE,
        payload,
      );
      const data = await response.json();

      if (!isSuccessfulApiResponse(response, data)) {
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: getApiErrorMessage(
            data,
            t("certificateOnboarding:update_failed"),
          ),
        });
        return;
      }

      const updateResult = data?.data ?? data ?? {};
      setUpdateData({
        transaction_id: updateResult.transaction_id || "",
        preview_svg_url: updateResult.preview_svg_url || "",
        template_svg_url: updateResult.template_svg_url || "",
        template_json: updateResult.template_json || {},
        subject_json: updateResult.subject_json || {},
      });
      setStep(3);

      Toast({
        type: "success",
        title: t("certificateOnboarding:success"),
        message: data.message || t("certificateOnboarding:update_success"),
      });
    } catch (error) {
      console.error("Certificate update failed", error);
      Toast({
        type: "error",
        title: t("certificateOnboarding:error"),
        message: t("certificateOnboarding:update_failed"),
      });
    } finally {
      setIsUpdateLoading(false);
    }
  };

  const handleCreateFormSchema = async () => {
    if (!canCreateFormSchema) return;

    setIsCreateFormLoading(true);

    try {
      const payload = {
        transaction_id: previewTransactionId || "",
      };
      const response = await POST(
        EntityConstants.CERTIFICATE_ONBOARDING_CREATE_FORM_SCHEMA,
        payload,
      );
      const data = await response.json();

      if (!isSuccessfulApiResponse(response, data)) {
        Toast({
          type: "error",
          title: t("certificateOnboarding:error"),
          message: getApiErrorMessage(
            data,
            t("certificateOnboarding:create_form_schema_failed"),
          ),
        });
        return;
      }

      const createResult = data?.data ?? data ?? {};
      setCreateFormData({
        transaction_id: createResult.transaction_id || "",
        certificate_name: createResult.certificate_name || "",
        certificate_description: createResult.certificate_description || "",
        entity_name: createResult.entity_name || "",
        preview_svg_url: createResult.preview_svg_url || "",
        template_svg_url: createResult.template_svg_url || "",
        template_form_id: createResult.template_form_id || "",
        subject_form_id: createResult.subject_form_id || "",
        qr_code_options: createResult.qr_code_options || {},
        context_json: createResult.context_json || {},
        credential_subject_properties_json:
          createResult.credential_subject_properties_json || {},
        sunbirdrc_schema_json: createResult.sunbirdrc_schema_json || {},
      });
      setStep(4);

      Toast({
        type: "success",
        title: t("certificateOnboarding:success"),
        message:
          data.message || t("certificateOnboarding:create_form_schema_success"),
      });
    } catch (error) {
      console.error("Create form schema failed", error);
      Toast({
        type: "error",
        title: t("certificateOnboarding:error"),
        message: t("certificateOnboarding:create_form_schema_failed"),
      });
    } finally {
      setIsCreateFormLoading(false);
    }
  };

  const continueDisabledTitle = useMemo(() => {
    if (step !== 1) return undefined;
    if (isIssuerProjectLoading) {
      return t("certificateOnboarding:issuer_project_loading");
    }
    if (!issuerProject) {
      return t("certificateOnboarding:issuer_project_failed");
    }
    if (
      !metadata.certificateName?.trim() ||
      !metadata.certificateDescription?.trim()
    ) {
      return t("certificateOnboarding:continue_disabled_metadata");
    }
    if (!metadata.folderName?.trim() || !isEntityNameValidated) {
      return t("certificateOnboarding:continue_disabled_entity_name");
    }
    if (!allAssetsUploaded) {
      return t("certificateOnboarding:continue_disabled_hint");
    }
    if (isAnyUploading) {
      return t("certificateOnboarding:continue_disabled_uploading");
    }
    if (!isFontPrepComplete) {
      return t("certificateOnboarding:font_prep_disabled_hint");
    }
    if (issuerProject === ISSUER_PROJECT.CERTIFY && !credentialFormat) {
      return t("certificateOnboarding:continue_disabled_format");
    }
    return undefined;
  }, [
    step,
    metadata.certificateName,
    metadata.certificateDescription,
    metadata.folderName,
    isEntityNameValidated,
    allAssetsUploaded,
    isAnyUploading,
    isFontPrepComplete,
    issuerProject,
    credentialFormat,
    isIssuerProjectLoading,
    t,
  ]);

  const previewDisabledTitle = useMemo(() => {
    if (step !== 2) return undefined;
    if (canContinueStep2) return undefined;
    if (!hasTransactionId) {
      return t("certificateOnboarding:continue_disabled_no_transaction");
    }
    return undefined;
  }, [step, canContinueStep2, hasTransactionId, t]);

  const renderStepContent = () => {
    if (step === 1) {
      return (
        <CertificateUploadStep
          assets={assets}
          setAssets={setAssets}
          uploadingSlots={uploadingSlots}
          setUploadingSlots={setUploadingSlots}
          isEntityNameValidated={isEntityNameValidated}
        />
      );
    }
    if (step === 2) {
      return (
        <CertificateConfigureStep
          cleanupData={cleanupData}
          variableConfig={variableConfig}
          setVariableConfig={setVariableConfig}
          qrCodeType={qrCodeType}
          onQrCodeTypeChange={setQrCodeType}
          userAddedVariableKeys={userAddedVariableKeys}
          onAddVariable={handleAddVariable}
          onRemoveVariable={handleRemoveVariable}
          t={t}
        />
      );
    }
    if (step === 3) {
      return <CertificatePreviewStep updateData={updateData} t={t} />;
    }
    return (
      <CertificateCompleteStep
        createFormData={createFormData}
        credentialFormat={cleanupData?.credential_format}
        onboardingScope={onboardingScope}
        onScopeChange={handleScopeChange}
        selectedCategory={selectedCategory}
        onCategorySelect={setSelectedCategory}
        onboardResult={onboardResult}
        isOnboarded={Boolean(onboardResult)}
        t={t}
      />
    );
  };

  return (
    <div
      className="flex flex-col w-full pb-8 pt-20 bg-cfSecondary min-h-screen"
      id={pageRootTestIds.certificateOnboarding}
    >
      <CertificateOnboardingHeader
        isTenantAdmin={isTenantAdmin}
        tenantCode={tenantCode}
        onBack={handleRequestExit}
      />

      <div className="flex-1 px-4 sm:px-6 py-6 mx-auto w-full">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-cfHeadingText">
            {contentTitle}
          </h1>
          <p className="text-sm text-cfGlobalText mt-1">{contentSubtitle}</p>
        </div>

        <div className="relative z-0 mb-6 rounded-2xl border border-cfCardBorder bg-cfSecondary shadow-sm px-4 sm:px-8">
          <CertificateOnboardingStepper currentStep={step} />
        </div>

        {step === 1 && (
          <>
            {issuerProjectError && !isIssuerProjectLoading && (
              <div
                className="rounded-xl border border-cfStatusBlockedText/30 bg-cfSecondary p-4 sm:p-5 mb-4"
                role="alert"
              >
                <p className="text-sm font-semibold text-cfHeadingText">
                  {t("certificateOnboarding:issuer_project_error_title")}
                </p>
                <p className="text-xs text-cfGlobalText mt-1">
                  {t("certificateOnboarding:issuer_project_failed")}
                </p>
                <button
                  type="button"
                  onClick={fetchIssuerProject}
                  className="mt-3 text-sm font-medium text-cfPrimary hover:text-cfPrimaryDark focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimary rounded"
                >
                  {t("certificateOnboarding:issuer_project_retry")}
                </button>
              </div>
            )}

            {issuerProject === ISSUER_PROJECT.CERTIFY &&
              !isIssuerProjectLoading && (
                <CredentialFormatPicker
                  value={credentialFormat}
                  onChange={(format) => {
                    setCredentialFormat(format);
                    setShowFormatError(false);
                  }}
                  error={
                    showFormatError
                      ? t("certificateOnboarding:credential_format_required")
                      : null
                  }
                  disabled={isCleanupLoading || isIssuerProjectLoading}
                  t={t}
                />
              )}

            <CertificateMetadataForm
              values={metadata}
              errors={showMetadataErrors ? metadataErrors : {}}
              onChange={handleMetadataChange}
              t={t}
              entitySuggestions={entitySuggestions}
              isSuggestionsLoading={isSuggestionsLoading}
              suggestionsError={suggestionsError}
              onFetchSuggestions={handleFetchEntitySuggestions}
              isEntityNameValidating={isEntityNameValidating}
              entityNameValidation={entityNameValidation}
              onValidateEntityName={handleValidateEntityName}
              onFolderNameChange={handleFolderNameChange}
              entityPickerDisabled={!canFetchSuggestions}
              onDescriptionBlur={handleDescriptionBlur}
            />

            <div
              id="cert-onboarding-assets-progress"
              className="inline-flex mb-3 px-3 py-1.5 rounded-full bg-cfSecondary text-sm font-medium text-cfHeadingText border border-cfCardBorder"
            >
              {t("certificateOnboarding:assets_progress", {
                count: uploadedCount,
              })}
            </div>
          </>
        )}

        <div className="mb-6">{renderStepContent()}</div>

        {step === 1 && (
          <div className="mb-6">
            <FontPreparationSection
              fontPrepData={fontPrepData}
              isFontPrepLoading={isFontPrepLoading}
              fontPrepError={fontPrepError}
              onPrepareFonts={handlePrepareFonts}
              canDetectFonts={canDetectFonts}
              manualFontCss={manualFontCss}
              isManualFontUploading={isManualFontUploading}
              onManualFontUpload={handleManualFontUpload}
              onManualFontRemove={handleManualFontRemove}
              disabled={!allAssetsUploaded || !isEntityNameValidated}
              t={t}
            />
          </div>
        )}

        <div className="flex justify-end">
          {step === 1 && (
            <button
              type="button"
              id="cert-onboarding-continue"
              onClick={handleContinueStep1}
              disabled={!canContinueStep1}
              title={continueDisabledTitle}
              aria-busy={isCleanupLoading}
              className={`${PRIMARY_BUTTON_CLASS} sm:ml-auto inline-flex items-center justify-center gap-2 min-w-[7rem]`}
            >
              {isCleanupLoading && (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              )}
              {isCleanupLoading
                ? t("certificateOnboarding:cleanup_loading")
                : t("certificateOnboarding:continue")}
            </button>
          )}

          {step === 2 && (
            <button
              type="button"
              id="cert-onboarding-continue-preview"
              onClick={handleContinueStep2}
              disabled={!canContinueStep2}
              title={previewDisabledTitle}
              aria-busy={isUpdateLoading}
              className={`${PRIMARY_BUTTON_CLASS} sm:ml-auto inline-flex items-center justify-center gap-2 min-w-[10rem]`}
            >
              {isUpdateLoading && (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              )}
              {isUpdateLoading
                ? t("certificateOnboarding:update_loading")
                : t("certificateOnboarding:continue_to_preview")}
            </button>
          )}

          {step === 3 && (
            <button
              type="button"
              id="cert-onboarding-create-form-schema"
              onClick={handleCreateFormSchema}
              disabled={!canCreateFormSchema}
              aria-busy={isCreateFormLoading}
              className={`${PRIMARY_BUTTON_CLASS} sm:ml-auto inline-flex items-center justify-center gap-2 min-w-[12rem]`}
            >
              {isCreateFormLoading && (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              )}
              {isCreateFormLoading
                ? t("certificateOnboarding:create_form_schema_loading")
                : t("certificateOnboarding:create_form_schema")}
            </button>
          )}

          {step === 4 && !onboardResult && (
            <button
              type="button"
              id="cert-onboarding-onboard-certificate"
              onClick={handleOnboardCertificate}
              disabled={!canOnboardCertificate}
              title={
                canOnboardCertificate
                  ? undefined
                  : t("certificateOnboarding:onboard_certificate_disabled_hint")
              }
              aria-busy={isOnboardingSubmitting}
              className={`${PRIMARY_BUTTON_CLASS} sm:ml-auto inline-flex items-center justify-center gap-2 min-w-[12rem]`}
            >
              {isOnboardingSubmitting && (
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              )}
              {isOnboardingSubmitting
                ? t("certificateOnboarding:onboard_certificate_loading")
                : t("certificateOnboarding:onboard_certificate")}
            </button>
          )}

          {step === 4 && onboardResult && (
            <button
              type="button"
              id="cert-onboarding-onboard-done"
              onClick={handleExitOnboarding}
              className={`${PRIMARY_BUTTON_CLASS} sm:ml-auto inline-flex items-center justify-center gap-2 min-w-[10rem]`}
            >
              {isTenantAdmin
                ? t("certificateOnboarding:back_to_organisations")
                : t("certificateOnboarding:back_to_tenants")}
            </button>
          )}
        </div>
      </div>

      <FullScreenLoadingOverlay
        isVisible={
          isCleanupLoading ||
          isUpdateLoading ||
          isCreateFormLoading ||
          isOnboardingSubmitting
        }
      />

      <CertificateOnboardSuccessModal
        isOpen={isOnboardSuccessModalOpen}
        onClose={() => setIsOnboardSuccessModalOpen(false)}
        onExit={handleExitOnboarding}
        onboardResult={onboardResult}
        certificateName={
          createFormData?.certificate_name?.trim() ||
          metadata.certificateName?.trim() ||
          ""
        }
        isTenantAdmin={isTenantAdmin}
        t={t}
      />

      <LeaveConfirmDialog
        isOpen={isLeaveDialogOpen}
        onStay={() => {
          leaveGuard.stay();
          setIsLeaveDialogOpen(false);
        }}
        onLeave={() => {
          setIsLeaveDialogOpen(false);
          leaveGuard.leave(handleExitOnboarding);
        }}
      />
    </div>
  );
}

export default CertificateOnboarding;
